import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Package, CheckCircle2 } from "lucide-react";

const EMPTY = {
  kennung: "",
  form: "GmbH",
  name: "",
  founded_year: new Date().getFullYear(),
  capital_chf: 20000,
  liberiert_percent: 100,
  price_chf: 8500,
  sitz: "",
  zweck_anpassbar: true,
  highlight: "",
  sold: false,
};

export default function AdminVorrats() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/vorrats");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (v) => {
    try {
      if (v.id) {
        await api.patch(`/admin/vorrats/${v.id}`, v);
      } else {
        await api.post("/admin/vorrats", v);
      }
      toast.success("Gespeichert");
      setEditing(null);
      load();
    } catch (e) { toast.error("Speichern fehlgeschlagen"); }
  };

  const del = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    try {
      await api.delete(`/admin/vorrats/${id}`);
      toast.success("Gelöscht");
      load();
    } catch { toast.error("Fehler"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="admin-vorrats">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1A2433] mb-1">Vorratsgesellschaften</h1>
          <p className="text-slate-600 text-sm">Aktuelle Angebote verwalten.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary" data-testid="vorrats-new">
          <Plus className="w-4 h-4" /> Neue
        </button>
      </div>

      {loading ? <div className="text-slate-500">Lade…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((v) => (
            <div key={v.id} className="border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#1A2433] flex items-center justify-center">
                    <Package className="w-5 h-5 text-[#DC2626]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#1A2433]">{v.name}</div>
                    <div className="text-xs text-slate-500">{v.form} · {v.founded_year} · {v.sitz}</div>
                  </div>
                </div>
                {v.sold && <span className="text-xs font-semibold bg-slate-200 text-slate-700 px-2 py-1 rounded-full">verkauft</span>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-3">
                <div>Kapital: <strong>CHF {v.capital_chf?.toLocaleString("de-CH")}</strong></div>
                <div>Liberiert: <strong>{v.liberiert_percent}%</strong></div>
                <div>Preis: <strong>CHF {v.price_chf?.toLocaleString("de-CH")}</strong></div>
                {v.highlight && <div className="col-span-2 text-[#991B1B] font-semibold">{v.highlight}</div>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(v)} className="text-sm text-[#991B1B] font-medium hover:underline flex items-center gap-1">
                  <Edit2 className="w-3 h-3" /> Bearbeiten
                </button>
                <button onClick={() => del(v.id)} className="text-sm text-red-600 font-medium hover:underline flex items-center gap-1 ml-auto">
                  <Trash2 className="w-3 h-3" /> Löschen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <VorratEditor value={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function VorratEditor({ value, onClose, onSave }) {
  const [v, setV] = useState(value);
  const set = (k, val) => setV((x) => ({ ...x, [k]: val }));

  return (
    <div className="fixed inset-0 z-[10001] bg-black/40 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold text-[#1A2433]">{v.id ? "Bearbeiten" : "Neu anlegen"}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="space-y-4">
          <Field label="Name" value={v.name} onChange={(x) => set("name", x)} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#1A2433] mb-2">Rechtsform</label>
              <select value={v.form} onChange={(e) => set("form", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white">
                <option value="GmbH">GmbH</option>
                <option value="AG">AG</option>
              </select>
            </div>
            <Field label="Gründungsjahr" type="number" value={v.founded_year} onChange={(x) => set("founded_year", Number(x))} />
          </div>
          <Field label="Kennung" value={v.kennung} onChange={(x) => set("kennung", x)} />
          <Field label="Sitz" value={v.sitz} onChange={(x) => set("sitz", x)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kapital CHF" type="number" value={v.capital_chf} onChange={(x) => set("capital_chf", Number(x))} />
            <Field label="Liberiert %" type="number" value={v.liberiert_percent} onChange={(x) => set("liberiert_percent", Number(x))} />
          </div>
          <Field label="Preis CHF" type="number" value={v.price_chf} onChange={(x) => set("price_chf", Number(x))} />
          <Field label="Highlight (optional)" value={v.highlight} onChange={(x) => set("highlight", x)} />
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={v.zweck_anpassbar} onChange={(e) => set("zweck_anpassbar", e.target.checked)} className="w-4 h-4 accent-[#991B1B]" />
            <span className="text-sm text-slate-700">Zweck/Name anpassbar</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={v.sold} onChange={(e) => set("sold", e.target.checked)} className="w-4 h-4 accent-[#991B1B]" />
            <span className="text-sm text-slate-700">Verkauft / vermittelt (wird mit „Vermittelt"-Badge weiter angezeigt – Social Proof)</span>
          </label>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Abbrechen</button>
          <button onClick={() => onSave(v)} className="btn-primary">
            <CheckCircle2 className="w-4 h-4" /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1A2433] mb-2">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]" />
    </div>
  );
}
